import { Dispatch, Fragment, SetStateAction, useState } from "react";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Badge,
  CardContent,
  Dialog,
  IconButton,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import { ListItemButton } from "../drawerUtils";
import TrendsPageIcon from "../../../../svgs/TrendsPageIcon";
import { SideDrawerContentStyles } from "../DrawerStyles";
import { getIsInternalUrl } from "../../../Dashboard/Tiles/CustomerTiles/customerTilesUtils";
import ReportIcon from "@mui/icons-material/Assessment";
import GridPageIcon from "@mui/icons-material/Dashboard";
import DashboardPageIcon from "@mui/icons-material/VerticalSplit";
import { useLocation, useNavigate } from "react-router";
import {
  useGetPageFromPath,
  useSummaryGroup,
  useDetailKey,
  useProcessesInVisibleGroups,
  useIsSerialProcess,
  useVisibleProcesses,
  useWorkScheduleIdOnCurrentPage,
  useIsAdmin,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { AheadBehindIcon } from "svgs";
import styled from "styled-components/macro";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { ONE_DAY_OF_MS } from "utils/timeUtils";
import { RavenLogoSvgOrWhitelabelLogo } from "components/Nav/RavenLogo/RavenLogo";
import {
  Analytics,
  Image,
  Download,
  ContactSupport,
  ViewTimeline,
  Settings,
  KeyboardArrowDown,
  Notifications,
  Troubleshoot,
} from "@mui/icons-material";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import ReactGA from "react-ga4";
import { darkBlue } from "constants/colors";
import { ConfigMenuItems } from "../../../Nav/ConfigMenuItems";
import {
  RequestAnalyticsTrialDialog,
  SightPageType,
} from "./RequestTrialDialog";
import Markdown from "markdown-to-jsx";
import { useLocalStorageState } from "utils/useLocalStorageState";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { getReportPageProcessOrGroupNameFromPath } from "components/DataGetter/ProcessGroupsGetter";
import { useSelector } from "store/useSelector";
import { lightGreyBackground } from "reports/constants/colors";
import LogRocket from "logrocket";

type LeftDrawerContentProps = {
  setIsLeftDrawerOpen: Dispatch<SetStateAction<boolean>>;
  isTest?: boolean;
};
// tslint:disable-next-line: no-big-function
const LeftDrawerContent = ({ setIsLeftDrawerOpen }: LeftDrawerContentProps) => {
  // Not using match here because the Nav is outside of the switch Router in App.tsx
  const ckey = useCustomerKey();
  const {
    isCustomerTilesPage,
    isGridLayoutPage,
    isHubletPage,
    isOperatorPage,
    isTimelinePage,
    isProcessTilesPage,
    isTrendsPage,
    isReportsPage,
    isSchedulePage,
    isTier0Page,
  } = useGetPageFromPath();

  const { search, pathname } = useLocation();
  const isTimelineOnlyPage = pathname.includes("/timeline");
  const processOrProcessGroupNameReportsPage =
    getReportPageProcessOrGroupNameFromPath(pathname);
  const isReportsListingPage =
    processOrProcessGroupNameReportsPage === "reports";
  const searchParams = new URLSearchParams(search);
  const urlWorkScheduleConfigId = searchParams.get("workSchedule");
  const processes = useVisibleProcesses();

  const pageName = pathname && pathname.split("/")[2];
  const isFlight = ["dashboard", "grid", "schedule"].includes(pageName);

  const isInternalUrl = getIsInternalUrl();
  const {
    flightLinkToGptRavenAi,
    flightEnableTier0Access,
    flightEnableSnapshotsAccess,
    flightEnableDataDumpsDownloaderAccess,
    flightAccessToServiceStatusPage,
  } = useFlagsWithDefaults();
  const isAdminUser = useIsAdmin();
  const [
    {
      general: {
        accessToTrendsPage,
        reportsEnabled,
        operatorPageEnabled,
        redirectFromHubletViewPageToGridPage,
        gridPageLinkToDashboardPage,
      },
    },
  ] = useFlightConfig();
  const isSerialProcess = useIsSerialProcess();

  const shouldShowReports = reportsEnabled && !isSchedulePage;
  const showTrendsButton =
    (isInternalUrl || accessToTrendsPage) && !isSchedulePage;

  //if is reportspage or trends page page get from url
  let workScheduleConfigId = "1";

  if (urlWorkScheduleConfigId && (isReportsPage || isTrendsPage)) {
    workScheduleConfigId = urlWorkScheduleConfigId;
  } else if (processes.length > 0) {
    workScheduleConfigId = processes[0].workScheduleConfig.id;
  }

  // otherwise getvisible processes and us the first work schedule id from that

  const summaryGroup = useSummaryGroup();
  const detailKey = useDetailKey();
  const { translate } = useFlightTranslation();

  const processIdOnSingleProcessPage = useProcessIdOnSingleProcessPage();
  const shouldShowOperatorPage =
    operatorPageEnabled === true ||
    (processIdOnSingleProcessPage &&
      Array.isArray(operatorPageEnabled) &&
      operatorPageEnabled.includes(processIdOnSingleProcessPage));

  const processesInVisibleGroups = useProcessesInVisibleGroups();
  const selectedProcessIdx = processesInVisibleGroups.findIndex(
    p => p.isSelected
  ); // there should only be one selected process if any

  /* on grid or dashboard (hublet/timeline/process tiles) pages, if the customer is allowed to link between grid <-> dashboard, show the link here
   * -> *don't* show on Group Tiles page because we need to be inside a group first to get to the grid page.
   */
  const isGridOrDashboardOrOperatorPage =
    isHubletPage ||
    isGridLayoutPage ||
    isTimelinePage ||
    isProcessTilesPage ||
    isOperatorPage;

  // show the links if the LD flag flight-grid-page-link-to-dashboard-page is enabled allowing linking between these pages
  const showGridAndDashboardLinks =
    (gridPageLinkToDashboardPage ||
      redirectFromHubletViewPageToGridPage ||
      operatorPageEnabled) &&
    isGridOrDashboardOrOperatorPage;
  // which process we should go to when clicking the Grid Page link
  const gridPageProcessIdx = detailKey
    ? detailKey // same process if we're already looking at one
    : isSerialProcess && selectedProcessIdx !== -1
      ? selectedProcessIdx // bottleneck process (if exists)
      : 0; // fallback to first process

  if (!ckey) {
    return null;
  }

  const showNbWeeklyLinks = ckey === "nb";
  const showMeritTier0 = flightEnableTier0Access;
  return (
    <SideDrawerContentStyles
      className="leftDrawer"
      data-component-name={"Left Drawer"}
    >
      <List className="list">
        <Link
          onClick={() => {
            setIsLeftDrawerOpen(false);
          }}
          to={`/`}
          className={`home ${isCustomerTilesPage ? " selected" : ""}`}
        >
          <ListItemButton
            className={`homeBlock${isCustomerTilesPage ? " selected" : ""}`}
          >
            <RavenLogoSvgOrWhitelabelLogo />
          </ListItemButton>
        </Link>
        <Link
          onClick={() => {
            setIsLeftDrawerOpen(false);
          }}
          to={
            showGridAndDashboardLinks
              ? `/${ckey}/schedule`
              : `/${ckey}/dashboard?workSchedule=${workScheduleConfigId}`
          }
          className={`${
            isFlight && !showGridAndDashboardLinks ? " selected" : ""
          }`}
        >
          <ListItemButton
            className={`flightBlock${
              isFlight && !showGridAndDashboardLinks ? " selected" : ""
            }`}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary={translate("Flight")} />
          </ListItemButton>
        </Link>
        {showGridAndDashboardLinks ? (
          <List disablePadding={true}>
            {/* link to Dashboard page */}
            <Link
              onClick={() => {
                setIsLeftDrawerOpen(false);
              }}
              to={`/${ckey}/dashboard/${summaryGroup}/${detailKey || 0}`}
              className={`${
                isHubletPage || isTimelinePage || isProcessTilesPage
                  ? " selected"
                  : ""
              }`}
            >
              <ListItemButton style={{ paddingLeft: 32 }}>
                <ListItemIcon>
                  <DashboardPageIcon style={{ transform: "rotate(180deg)" }} />
                </ListItemIcon>
                <ListItemText primary={translate("Dashboard")} />
              </ListItemButton>
            </Link>

            {/* link to Grid page */}
            {(gridPageLinkToDashboardPage ||
              redirectFromHubletViewPageToGridPage) && (
              <Link
                onClick={() => {
                  setIsLeftDrawerOpen(false);
                }}
                to={`/${ckey}/grid/${summaryGroup}/${gridPageProcessIdx}`}
                className={`${isGridLayoutPage ? " selected" : ""}`}
              >
                <ListItemButton style={{ paddingLeft: 32 }}>
                  <ListItemIcon>
                    <GridPageIcon />
                  </ListItemIcon>
                  <ListItemText primary={translate("Grid")} />
                </ListItemButton>
              </Link>
            )}
            {/* link to Operator page */}
            {shouldShowOperatorPage && (
              <LinkToOperatorPage setIsLeftDrawerOpen={setIsLeftDrawerOpen} />
            )}
            {isAdminUser && (
              <Link
                onClick={() => {
                  setIsLeftDrawerOpen(false);
                }}
                to={`/${ckey}/timeline/${summaryGroup}/${detailKey}?workSchedule=${workScheduleConfigId}`}
                style={isTimelineOnlyPage ? { pointerEvents: "none" } : {}}
                className={isTimelineOnlyPage ? "selected" : ""}
              >
                <ListItemButton style={{ paddingLeft: 32 }}>
                  <ListItemIcon>
                    <ViewTimeline />
                  </ListItemIcon>
                  <ListItemText primary={translate("Timeline")} />
                </ListItemButton>
              </Link>
            )}
          </List>
        ) : null}
        {/* <List>
              <ListItemButton>
                <ListItemIcon>
                  // this is a placeholder for the list items that will be added in the flightBlock for its implementation
                </ListItemIcon>
              </ListItemButton>
            </List> */}

        {/* <ListItemButton className="Alerts" >
              <ListItemIcon>
                <NotificationIcon />
              </ListItemIcon>
              <ListItemText primary={"Alerts"} />
            </ListItemButton> */}

        {shouldShowReports && (
          <Link
            onClick={() => {
              setIsLeftDrawerOpen(false);
            }}
            to={`/${ckey}/reports?workSchedule=${workScheduleConfigId}`}
            style={isReportsListingPage ? { pointerEvents: "none" } : {}}
          >
            <ListItemButton
              className={`reportsBlock${isReportsPage ? " selected" : ""}`}
            >
              <ListItemIcon>
                <ReportIcon />
              </ListItemIcon>
              <ListItemText primary={translate("Reports")} />
            </ListItemButton>
          </Link>
        )}

        {showTrendsButton && !showNbWeeklyLinks && (
          <Link
            onClick={() => {
              setIsLeftDrawerOpen(false);
            }}
            to={`/${ckey}/trends?workSchedule=${workScheduleConfigId}`}
            style={isTrendsPage ? { pointerEvents: "none" } : {}}
          >
            <ListItemButton
              className={`trendsBlock${isTrendsPage ? " selected" : ""}`}
            >
              <ListItemIcon>
                <TrendsPageIcon />
              </ListItemIcon>
              <ListItemText primary={translate("Trends")} />
            </ListItemButton>
          </Link>
        )}
        {showTrendsButton && showNbWeeklyLinks && (
          <NobelWeeklyTeamTrends {...{ setIsLeftDrawerOpen }} />
        )}
        {showMeritTier0 && (
          <Link
            onClick={() => {
              setIsLeftDrawerOpen(false);
            }}
            to={`/${ckey}/tier0?workSchedule=${workScheduleConfigId}&rangeSelectorHidden=true`}
            style={isTier0Page ? { pointerEvents: "none" } : {}}
          >
            <ListItemButton
              className={`trendsBlock${isTier0Page ? " selected" : ""}`}
            >
              <ListItemIcon>
                <TrendsPageIcon />
              </ListItemIcon>
              <ListItemText primary={translate("Tier 0")} />
            </ListItemButton>
          </Link>
        )}
        {/* internal link to Snapshots page */}
        {flightEnableSnapshotsAccess ? (
          <Link
            onClick={() => {
              setIsLeftDrawerOpen(false);
            }}
            to={`/${ckey}/snapshots?workSchedule=${workScheduleConfigId}`}
            className={`${
              window.location.pathname.includes("snapshots") ? " selected" : ""
            }`}
          >
            <ListItemButton>
              <ListItemIcon>
                <Image />
              </ListItemIcon>
              <ListItemText primary={translate("Snapshots")} />
            </ListItemButton>
          </Link>
        ) : null}
        {flightEnableDataDumpsDownloaderAccess ? (
          <Link
            onClick={() => {
              setIsLeftDrawerOpen(false);
            }}
            to={`/${ckey}/dataDumps?workSchedule=${workScheduleConfigId}`}
            className={`${
              window.location.pathname.includes("dataDumps") ? " selected" : ""
            }`}
          >
            <ListItemButton>
              <ListItemIcon>
                <Download />
              </ListItemIcon>
              <ListItemText primary={translate("Data Dumps")} />
            </ListItemButton>
          </Link>
        ) : null}

        <SightLinksList
          {...{
            setIsLeftDrawerOpen,
          }}
        />
        <ConfigurationMenu
          setIsLeftDrawerOpen={setIsLeftDrawerOpen}
          ckey={ckey}
        />
        <Link
          onClick={() => {
            setIsLeftDrawerOpen(false);
          }}
          to={`https://support.raven.ai`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ListItemButton>
            <ListItemIcon>
              <ContactSupport />
            </ListItemIcon>
            <ListItemText primary={translate("Support")} />
          </ListItemButton>
        </Link>
      </List>

      {/* This is a hidden link that should eventually be deleted */}
      {flightLinkToGptRavenAi && (
        <List
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
          }}
        >
          <a
            onClick={() => {
              setIsLeftDrawerOpen(false);
            }}
            href={`https://gpt.raven.ai/?utm_source=flight#${window.location.href}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div style={{ width: "10em", height: "4em" }}></div>
          </a>
        </List>
      )}

      {/* Link to download-kiosk page at the very bottom of the drawer */}
      <List
        className="bottom-of-drawer-list"
        style={{
          marginTop: "auto",
          borderTop: "1px solid rgba(255, 255, 255, 0.12)",
          paddingTop: "8px",
        }}
      >
        {flightAccessToServiceStatusPage && (
          <Link
            onClick={() => {
              setIsLeftDrawerOpen(false);
            }}
            to={`/${ckey}/service-status`}
            style={{ color: "#FFF", textDecoration: "none" }}
          >
            <ListItemButton>
              <ListItemIcon>
                <Troubleshoot />
              </ListItemIcon>
              <ListItemText primary={translate("Service Status")} />
            </ListItemButton>
          </Link>
        )}
        <Link
          onClick={() => {
            setIsLeftDrawerOpen(false);
          }}
          to={`/get-kiosk`}
          style={{ color: "#FFF", textDecoration: "none" }}
        >
          <ListItemButton>
            <ListItemIcon>
              <Download />
            </ListItemIcon>
            <ListItemText primary={translate("Get Kiosk App")} />
          </ListItemButton>
        </Link>
      </List>
    </SideDrawerContentStyles>
  );
};

export default LeftDrawerContent;

function NobelWeeklyTeamTrends({
  setIsLeftDrawerOpen,
}: {
  setIsLeftDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const startOfLastWeek = startOfToday - 7 * ONE_DAY_OF_MS;
  return (
    <WeeklyTeamTrendsStyles>
      <ListItem className="title">Weekly Team Trends</ListItem>
      <Link
        onClick={() => {
          setIsLeftDrawerOpen(false);
        }}
        to={`/nb/trends?binSize=days&ge=${startOfLastWeek}&lt=${startOfToday}&group=Team%20A`}
      >
        <ListItemButton className={`weeklyTeamTrendsBlock`}>
          <ListItemIcon>
            <TrendsPageIcon />
          </ListItemIcon>
          <ListItemText primary={"Team A"} />
        </ListItemButton>
      </Link>
      <Link
        onClick={() => {
          setIsLeftDrawerOpen(false);
        }}
        to={`/nb/trends?binSize=days&ge=${startOfLastWeek}&lt=${startOfToday}&group=Team%20B`}
      >
        <ListItemButton className={`weeklyTeamTrendsBlock`}>
          <ListItemIcon>
            <TrendsPageIcon />
          </ListItemIcon>
          <ListItemText primary={"Team B"} />
        </ListItemButton>
      </Link>
      <Link
        onClick={() => {
          setIsLeftDrawerOpen(false);
        }}
        to={`/nb/trends?binSize=days&ge=${startOfLastWeek}&lt=${startOfToday}&group=Team%20C`}
      >
        <ListItemButton className={`weeklyTeamTrendsBlock`}>
          <ListItemIcon>
            <TrendsPageIcon />
          </ListItemIcon>
          <ListItemText primary={"Team C"} />
        </ListItemButton>
      </Link>
      <Link
        onClick={() => {
          setIsLeftDrawerOpen(false);
        }}
        to={`/nb/trends?binSize=days&ge=${startOfLastWeek}&lt=${startOfToday}&group=Citizen`}
      >
        <ListItemButton className={`weeklyTeamTrendsBlock`}>
          <ListItemIcon>
            <TrendsPageIcon />
          </ListItemIcon>
          <ListItemText primary={"Citizen"} />
        </ListItemButton>
      </Link>
    </WeeklyTeamTrendsStyles>
  );
}

const WeeklyTeamTrendsStyles = styled.div`
  margin-left: 1em;
  .title {
    margin-left: -12px;
    margin-bottom: 6px;
  }
`;

function EyeIcon(props) {
  return (
    <svg
      {...props}
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 576 512"
      height="200px"
      width="200px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M288 144a110.94 110.94 0 0 0-31.24 5 55.4 55.4 0 0 1 7.24 27 56 56 0 0 1-56 56 55.4 55.4 0 0 1-27-7.24A111.71 111.71 0 1 0 288 144zm284.52 97.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0 0-29.19zM288 400c-98.65 0-189.09-55-237.93-144C98.91 167 189.34 112 288 112s189.09 55 237.93 144C477.1 345 386.66 400 288 400z"></path>
    </svg>
  );
}
function ThroughputIcon(props) {
  return (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 1024 1024"
      height="200px"
      width="200px"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M888 792H200V168c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v688c0 4.4 3.6 8 8 8h752c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8zM305.8 637.7c3.1 3.1 8.1 3.1 11.3 0l138.3-137.6L583 628.5c3.1 3.1 8.2 3.1 11.3 0l275.4-275.3c3.1-3.1 3.1-8.2 0-11.3l-39.6-39.6a8.03 8.03 0 0 0-11.3 0l-230 229.9L461.4 404a8.03 8.03 0 0 0-11.3 0L266.3 586.7a8.03 8.03 0 0 0 0 11.3l39.5 39.7z"></path>
    </svg>
  );
}

function SightLinksList({
  setIsLeftDrawerOpen,
}: {
  setIsLeftDrawerOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const { search, pathname } = useLocation();
  const currentPathAndQuery = pathname + search;

  const { flightLinkToSightAnalytics } = useFlagsWithDefaults();
  const [
    {
      general: {
        powerBiReportIdThroughput,
        powerBiReports,
        accessToSightAnalytics,
      },
    },
  ] = useFlightConfig();
  const { translate } = useFlightTranslation();

  const [requestAnalyticsTrialDialogOpen, setRequestAnalyticsTrialDialogOpen] =
    useState<null | SightPageType>(null);
  const ckey = useCustomerKey();
  if (flightLinkToSightAnalytics === "hidden" && !accessToSightAnalytics) {
    return null;
  }
  return (
    <>
      <List disablePadding={true}>
        <div style={{ pointerEvents: "none" }}>
          <ListItemButton>
            <ListItemIcon>
              <div
                style={{
                  width: 18,
                  height: 18,
                  background: "white",
                  borderRadius: 2,
                  transform: "translateX(2px)",
                }}
              >
                <EyeIcon
                  style={{
                    color: darkBlue,
                    width: "100%",
                    height: "100%",
                    padding: 2,
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </ListItemIcon>
            <ListItemText primary={translate("Sight")} />
          </ListItemButton>
        </div>
        <SightLinkLastUpdateNotification sightPage={"analytics"}>
          <Link
            onClick={() => {
              LogRocket.track("Clicked Sight Analytics");
              if (
                flightLinkToSightAnalytics === "trial" &&
                !accessToSightAnalytics
              ) {
                setRequestAnalyticsTrialDialogOpen("analytics");
                return;
              }
              setIsLeftDrawerOpen(false);
              console.log("tracking 'Clicked Sight Analytics' event");
              ReactGA.event({
                category: "User",
                action: "Clicked Sight Analytics",
              });
            }}
            to={
              flightLinkToSightAnalytics === "enabled" || accessToSightAnalytics
                ? `/${ckey}/sight-analytics`
                : currentPathAndQuery
            }
            className={`${
              window.location.pathname.includes("sight-analytics")
                ? " selected"
                : ""
            }`}
          >
            <ListItemButton style={{ paddingLeft: 32 }}>
              <ListItemIcon>
                <Analytics />
              </ListItemIcon>
              <ListItemText primary={translate("Analytics")} />
            </ListItemButton>
          </Link>
        </SightLinkLastUpdateNotification>
        <SightLinkLastUpdateNotification sightPage={"throughput"}>
          <Link
            onClick={e => {
              LogRocket.track("Clicked Sight Throughput");
              if (!powerBiReportIdThroughput) {
                setRequestAnalyticsTrialDialogOpen("throughput");
                return;
              }
              setIsLeftDrawerOpen(false);
              console.log("tracking 'Clicked Sight Throughput' event");
              ReactGA.event({
                category: "User",
                action: "Clicked Sight Throughput",
              });
            }}
            to={
              powerBiReportIdThroughput
                ? `/${ckey}/sight-throughput`
                : currentPathAndQuery
            }
            className={`${
              window.location.pathname.includes("sight-throughput")
                ? " selected"
                : ""
            }`}
          >
            <ListItemButton style={{ paddingLeft: 32 }}>
              <ListItemIcon>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    background: "white",
                    borderRadius: 2,
                    transform: "translateX(3px)",
                  }}
                >
                  <ThroughputIcon
                    style={{
                      color: darkBlue,
                      width: "100%",
                      height: "100%",
                      padding: 2,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </ListItemIcon>
              <ListItemText primary={translate("Throughput")} />
            </ListItemButton>
          </Link>
        </SightLinkLastUpdateNotification>

        {/* Render additional PowerBI report links from the powerBiReports array */}
        {powerBiReports
          .filter(report => report.id && report.name)
          .map((report, index) => (
            <SightLinkLastUpdateNotification
              key={`powerbi-${index}`}
              sightPage={"throughput"}
            >
              <Link
                onClick={e => {
                  LogRocket.track(`Clicked Sight ${report.name}`);
                  setIsLeftDrawerOpen(false);
                  console.log(`tracking 'Clicked Sight ${report.name}' event`);
                  ReactGA.event({
                    category: "User",
                    action: `Clicked Sight ${report.name}`,
                  });
                }}
                to={`/${ckey}/sight/${encodeURIComponent(report.id)}`}
                className={`${window.location.pathname.includes(`sight/${encodeURIComponent(report.id)}`) ? " selected" : ""}`}
              >
                <ListItemButton style={{ paddingLeft: 32 }}>
                  <ListItemIcon>
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        background: "white",
                        borderRadius: 2,
                        transform: "translateX(3px)",
                      }}
                    >
                      <ThroughputIcon
                        style={{
                          color: darkBlue,
                          width: "100%",
                          height: "100%",
                          padding: 2,
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </ListItemIcon>
                  <ListItemText primary={translate(report.name)} />
                </ListItemButton>
              </Link>
            </SightLinkLastUpdateNotification>
          ))}

        {/* <SightLinkLastUpdateNotification sightPage={"logbook"}>
          <Link
            onClick={e => {
              if (!powerBiReportIdLogbook) {
                setRequestAnalyticsTrialDialogOpen("logbook");
                return;
              }
              setIsLeftDrawerOpen(false);
              console.log("tracking 'Clicked Sight Logbook' event");
              ReactGA.event({
                category: "User",
                action: "Clicked Sight Logbook",
              });
            }}
            to={
              powerBiReportIdLogbook
                ? `/${ckey}/sight-logbook`
                : currentPathAndQuery
            }
            className={`${
              window.location.pathname.includes("sight-logbook")
                ? " selected"
                : ""
            }`}
          >
            <ListItemButton style={{ paddingLeft: 32 }}>
              <ListItemIcon>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    background: "white",
                    borderRadius: 2,
                    transform: "translateX(3px)",
                  }}
                >
                  <Equalizer
                    style={{
                      color: darkBlue,
                      width: "100%",
                      height: "100%",
                      padding: 2,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </ListItemIcon>
              <ListItemText primary={translate("Logbook")} />
            </ListItemButton>
          </Link>
        </SightLinkLastUpdateNotification> */}
      </List>
      <RequestAnalyticsTrialDialog
        open={requestAnalyticsTrialDialogOpen}
        handleClose={() => {
          setRequestAnalyticsTrialDialogOpen(null);
        }}
      />
    </>
  );
}

function SightLinkLastUpdateNotification({
  children,
  sightPage,
}: {
  children: React.ReactNode;
  sightPage: SightPageType;
}) {
  const [
    {
      general: { powerBiUpdates },
    },
  ] = useFlightConfig();
  const { user } = useAuth0();
  const lastUpdate = powerBiUpdates?.[sightPage];
  const markdownRaw = lastUpdate.markdown;
  const markdownText = markdownRaw
    ?.replaceAll("{{name}}", user?.name ?? "")
    .replaceAll("{{user}}", user?.name ?? "");
  const [open, setOpen] = useState(false);
  const { translate } = useFlightTranslation();

  // only show the bell icon + badge if there's an update that the user hasn't viewed yet
  const [updateStored, setUpdateStored] = useLocalStorageState(
    `sight-update-${sightPage}`,
    ""
  );
  const showUpdateBadge = updateStored.markdown !== lastUpdate.markdown;
  const showNotification = lastUpdate.markdown;
  return (
    <>
      <div style={{ display: "flex" }}>
        <div style={{ flexGrow: 1 }}>{children}</div>
        {showNotification ? (
          <div style={{ height: 48, width: 48 }}>
            <Tooltip
              title={showUpdateBadge ? translate("What's new") : ""}
              disableInteractive={true}
            >
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  setOpen(true);
                  setUpdateStored(lastUpdate);
                }}
                style={{
                  color: "white",
                  height: "100%",
                  padding: 12,
                  opacity: showUpdateBadge ? 1 : 0.5,
                }}
              >
                <Badge
                  badgeContent={showUpdateBadge ? 1 : undefined}
                  color={"error"}
                >
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>
          </div>
        ) : null}
      </div>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <CardContent style={{ minWidth: 280 }}>
          <MarkdownStyles>
            {markdownText.split("\n\n").map((markdownChunk, i) => {
              return (
                <Fragment key={i}>
                  <div className="spacer" style={{ height: 12 }}></div>
                  {markdownChunk.split("\n").map((line, i) => (
                    <Markdown options={{ forceBlock: true }} key={i}>
                      {line}
                    </Markdown>
                  ))}
                </Fragment>
              );
            })}
          </MarkdownStyles>
          <br />
          <div style={{ margin: "0 0.5em 0 auto", width: "fit-content" }}>
            <a href={`mailto:${lastUpdate.mailto || "support@raven.ai"}`}>
              {lastUpdate.mailto || "support@raven.ai"}
            </a>
          </div>
        </CardContent>
      </Dialog>
    </>
  );
}

// Configuration Menu Component
type ConfigurationMenuProps = {
  setIsLeftDrawerOpen: Dispatch<SetStateAction<boolean>>;
  ckey: string;
};

function ConfigurationMenu({
  setIsLeftDrawerOpen,
  ckey,
}: ConfigurationMenuProps) {
  const { translate } = useFlightTranslation();

  // For Configuration menu
  const [configMenuAnchorEl, setConfigMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const configMenuOpen = Boolean(configMenuAnchorEl);
  const handleConfigMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setConfigMenuAnchorEl(event.currentTarget);
  };
  const handleConfigMenuClose = () => {
    setConfigMenuAnchorEl(null);
  };

  return (
    <div className="configuration-menu">
      <ListItemButton onClick={handleConfigMenuClick}>
        <ListItemIcon>
          <Settings />
        </ListItemIcon>
        <ListItemText primary={translate("View Configuration")} />
        <KeyboardArrowDown
          style={{
            transform: configMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        />
      </ListItemButton>
      <StyledConfigurationMenu
        anchorEl={configMenuAnchorEl}
        open={configMenuOpen}
        onClose={handleConfigMenuClose}
        MenuListProps={{
          "aria-labelledby": "config-menu-button",
        }}
        PaperProps={{
          style: {
            width: 220,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <ConfigMenuItems
          ckey={ckey}
          pathname={location.pathname}
          handleClose={handleConfigMenuClose}
          onItemClick={() => setIsLeftDrawerOpen(false)}
        />
      </StyledConfigurationMenu>
    </div>
  );
}

const StyledConfigurationMenu = styled(Menu)`
  a {
    color: #007eae !important;
  }
  .MuiSvgIcon-root {
    color: ${darkBlue};
  }
`;

const MarkdownStyles = styled.div`
  h1 {
    font-size: 1.5em;
  }
  h2 {
    font-size: 1.2em;
  }
  h3 {
    font-size: 1em;
  }
  h4 {
    font-size: 0.8em;
  }
  h5 {
    font-size: 0.6em;
  }
  h6 {
    font-size: 0.4em;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: bold;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ol,
  ul,
  img,
  p {
    margin-bottom: 0.5em;
  }
  img {
    display: flex;
    margin: 16px auto;
  }
  p,
  a,
  ul,
  ol {
    font-size: 1em;
  }

  li {
    font-size: 1em;
    list-style-type: disc;
    margin-left: 1.2em;
  }
`;

function LinkToOperatorPage({
  setIsLeftDrawerOpen,
}: {
  setIsLeftDrawerOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const { translate } = useFlightTranslation();
  const ckey = useCustomerKey();
  const summaryGroup = useSummaryGroup();
  const detailKey = useDetailKey();
  const { isHubletPage } = useGetPageFromPath();
  const workScheduleConfigId = useWorkScheduleIdOnCurrentPage();
  const allGroups = useSelector(state => state.legacy.allGroups);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [
    {
      general: { operatorPageEnabled },
    },
  ] = useFlightConfig();
  // if we're on Hublet page, clicking left side immediately goes to equivalent ahead/behind page
  const equivalentAheadBehindUrl = `/${ckey}/operator/${summaryGroup}/${detailKey}?workScheduleConfigId=${workScheduleConfigId}`;
  const navigate = useNavigate();
  const allowedProcessIds = Array.isArray(operatorPageEnabled)
    ? operatorPageEnabled
    : [];
  return (
    <div>
      <ListItemButton
        onClick={e => {
          if (isHubletPage) {
            navigate(equivalentAheadBehindUrl);
            setIsLeftDrawerOpen(false);
          } else {
            handleClick(e);
          }
        }}
        style={{ paddingLeft: 32 }}
      >
        <ListItemIcon>
          <AheadBehindIcon style={{ padding: "4px" }} />
        </ListItemIcon>
        <ListItemText primary={translate("Ahead_/_Behind")} />
        <IconButton
          onClick={handleClick}
          style={{ color: "white", marginRight: -16, marginLeft: -16 }}
        >
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </ListItemButton>
      <Menu anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
        {Object.entries(allGroups).map(([groupIdx, processGroup]) => (
          <div key={groupIdx}>
            <ListItemText
              style={{
                paddingLeft: 16,
                fontStyle: "italic",
              }}
              primary={processGroup.shortName}
            />
            {processGroup.processes
              .filter(
                process =>
                  allowedProcessIds.length === 0 ||
                  allowedProcessIds.includes(process.id)
              )
              .map((process, processIdx) => {
                const isSelected =
                  processGroup.summaryGroup === summaryGroup &&
                  String(processIdx) === detailKey;
                return (
                  <Link
                    key={process.id}
                    to={`/${ckey}/operator/${processGroup.summaryGroup}/${processIdx}?workSchedule=${workScheduleConfigId}`}
                    onClick={() => setIsLeftDrawerOpen(false)}
                  >
                    <MenuItem
                      onClick={handleClose}
                      style={{
                        paddingLeft: 32,
                        ...(isSelected
                          ? {
                              background: lightGreyBackground,
                            }
                          : {}),
                      }}
                    >
                      <ListItemText primary={process.name} />
                    </MenuItem>
                  </Link>
                );
              })}
          </div>
        ))}
      </Menu>
    </div>
  );
}
